/**
 * GroupCourse Builder List Page - Sprint 1
 * 
 * Pantalla de administración para gestionar cursos grupales (18 semanas B1-B6).
 * Solo accesible por admins y teachers.
 * 
 * Features:
 * - Listar todos los cursos grupales con % de completitud
 * - Crear nuevos cursos grupales
 * - Editar cursos (draft)
 * - Eliminar cursos (draft only)
 * - Vista previa y publicación
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md
 * @see SISTEMA_GRUPAL_DOCUMENTACION_COMPLETA.md
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Pencil, 
  Eye, 
  Trash2, 
  Loader,
  AlertCircle,
  BookOpen,
  X,
  Users,
  Calendar,
  CheckCircle2
} from 'lucide-react';
import { 
  listGroupCourses, 
  createGroupCourse, 
  deleteGroupCourse 
} from '../../services/adminGroupCoursesApi';
import { useAuth } from '../../context/AuthContext';

const GroupCourseBuilderListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Estados
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  // Form states
  const [formData, setFormData] = useState({
    courseId: '',
    title: '',
    shortDescription: '',
    estimatedHours: 72, // 18 semanas × 4 horas promedio
    skills: '',
    totalWeeks: 18,
    minGroupSize: 3,
    maxGroupSize: 8
  });
  const [formError, setFormError] = useState(null);

  // Verificar permisos
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'teacher') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Cargar cursos al montar
  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listGroupCourses();
      setCourses(response.courses || []);
    } catch (err) {
      console.error('Error cargando cursos grupales:', err);
      setError('No se pudo cargar la lista de cursos grupales');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    setFormError(null);
    setCreating(true);

    try {
      // Parsear skills (separadas por coma)
      const skillsArray = formData.skills
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      const payload = {
        courseId: formData.courseId.trim().toLowerCase(),
        title: formData.title.trim(),
        shortDescription: formData.shortDescription.trim(),
        estimatedHours: parseInt(formData.estimatedHours, 10),
        skills: skillsArray,
        groupCourseConfig: {
          totalWeeks: parseInt(formData.totalWeeks, 10),
          minGroupSize: parseInt(formData.minGroupSize, 10),
          maxGroupSize: parseInt(formData.maxGroupSize, 10),
          enableCrossFeedback: true,
          enableAICoach: true,
          weights: {
            deliverable: 50,
            crossFeedback: 30,
            aiCoachFeedback: 20
          }
        }
      };

      const response = await createGroupCourse(payload);
      
      // Cerrar modal y refrescar
      setShowCreateModal(false);
      setFormData({
        courseId: '',
        title: '',
        shortDescription: '',
        estimatedHours: 72,
        skills: '',
        totalWeeks: 18,
        minGroupSize: 3,
        maxGroupSize: 8
      });
      
      await loadCourses();
      
      // Redirigir a editar
      navigate(`/admin/group-course-builder/${response.course.courseId}/edit`);
    } catch (err) {
      setFormError(err.response?.data?.error || err.message || 'Error creando curso');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este curso? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setDeleting(courseId);
      await deleteGroupCourse(courseId);
      await loadCourses();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error eliminando curso');
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700 border-gray-300',
      published: 'bg-green-100 text-green-700 border-green-300',
      archived: 'bg-red-100 text-red-700 border-red-300'
    };

    const labels = {
      draft: 'Borrador',
      published: 'Publicado',
      archived: 'Archivado'
    };

    return (
      <span className={`px-2 py-1 rounded-md text-xs font-medium border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getCompletionColor = (percentage) => {
    if (percentage < 30) return 'bg-red-500';
    if (percentage < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Filtrar cursos
  const filteredCourses = filterStatus === 'all' 
    ? courses 
    : courses.filter(c => c.status === filterStatus);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-8 h-8 text-purple-600" />
              Group Course Builder
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Cursos grupales colaborativos de 18 semanas con bloques B1-B6
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nuevo Curso Grupal
          </button>
        </div>

        {/* Filtros */}
        <div className="mt-6 flex gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === 'all'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Todos ({courses.length})
          </button>
          <button
            onClick={() => setFilterStatus('draft')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === 'draft'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Borradores ({courses.filter(c => c.status === 'draft').length})
          </button>
          <button
            onClick={() => setFilterStatus('published')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === 'published'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Publicados ({courses.filter(c => c.status === 'published').length})
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="max-w-7xl mx-auto flex items-center justify-center py-12">
          <Loader className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="max-w-7xl mx-auto bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Lista de cursos */}
      {!loading && !error && (
        <div className="max-w-7xl mx-auto">
          {filteredCourses.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No hay cursos grupales
              </h3>
              <p className="text-gray-500 mb-6">
                Comienza creando tu primer curso grupal de 18 semanas
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Crear Primer Curso
              </button>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredCourses.map((course) => (
                <div
                  key={course.courseId}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                >
                  {/* Header */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {course.title}
                      </h3>
                      {getStatusBadge(course.status)}
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {course.shortDescription}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {course.totalWeeks || 18} semanas
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {course.groupCourseConfig?.minGroupSize || 3}-{course.groupCourseConfig?.maxGroupSize || 8} personas
                      </span>
                    </div>
                  </div>

                  {/* Progreso */}
                  <div className="px-6 py-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-700">
                        Configuración
                      </span>
                      <span className="text-xs font-bold text-gray-900">
                        {course.completionPercentage || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${getCompletionColor(
                          course.completionPercentage || 0
                        )}`}
                        style={{ width: `${course.completionPercentage || 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {course.configuredWeeks || 0} de {course.totalWeeks || 18} semanas configuradas
                    </p>
                  </div>

                  {/* Acciones */}
                  <div className="p-4 flex items-center justify-end gap-2 border-t border-gray-100">
                    {/* Botón Editar - Disponible para todos los cursos */}
                    <button
                      onClick={() => navigate(`/admin/group-course-builder/${course.courseId}/edit`)}
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 p-2 rounded-lg transition-colors"
                      title={course.status === 'published' ? "Editar (Publicado)" : "Editar"}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    
                    {/* Botón Vista Previa - Disponible para cursos publicados */}
                    {course.status === 'published' && (
                      <button
                        onClick={() => navigate(`/admin/group-course-builder/${course.courseId}/preview`)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                        title="Vista Previa"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    
                    {/* Botón Eliminar - Solo para borradores */}
                    {course.status === 'draft' && (
                      <button
                        onClick={() => handleDelete(course.courseId)}
                        disabled={deleting === course.courseId}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors disabled:opacity-50"
                        title="Eliminar"
                      >
                        {deleting === course.courseId ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal Crear Curso */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Crear Nuevo Curso Grupal
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormError(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleCreateCourse} className="p-6 space-y-6">
              {/* Error */}
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-red-700 text-sm">{formError}</p>
                </div>
              )}

              {/* Metadata Básica */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.courseId}
                    onChange={(e) => setFormData({ ...formData, courseId: e.target.value })}
                    placeholder="ej: gestion-proyectos-2024"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                    pattern="[a-z0-9-]+"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Solo letras minúsculas, números y guiones
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título del Curso <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="ej: Gestión de Proyectos Ágiles"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción Corta <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.shortDescription}
                    onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                    placeholder="Breve descripción del curso (1-2 líneas)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Horas Estimadas
                    </label>
                    <input
                      type="number"
                      value={formData.estimatedHours}
                      onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Semanas
                    </label>
                    <input
                      type="number"
                      value={formData.totalWeeks}
                      onChange={(e) => setFormData({ ...formData, totalWeeks: e.target.value })}
                      min="1"
                      max="52"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tamaño Mínimo Grupo
                    </label>
                    <input
                      type="number"
                      value={formData.minGroupSize}
                      onChange={(e) => setFormData({ ...formData, minGroupSize: e.target.value })}
                      min="2"
                      max="20"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tamaño Máximo Grupo
                    </label>
                    <input
                      type="number"
                      value={formData.maxGroupSize}
                      onChange={(e) => setFormData({ ...formData, maxGroupSize: e.target.value })}
                      min="2"
                      max="20"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Skills (separadas por coma)
                  </label>
                  <input
                    type="text"
                    value={formData.skills}
                    onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                    placeholder="ej: Planificación, Liderazgo, Comunicación"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Acciones */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormError(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  {creating ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Crear Curso
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupCourseBuilderListPage;
